import React, { useEffect, useState } from "react";
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import Api from "../../customlib/Api";
import Global from "../../customlib/Global";
import livechatApi from "./livechatApi";
import { LIVECHAT_PARTNER_BRAND, CORPORATE_BRAND } from "./livechatConstants";
import { getAvatarColor, getInitials } from "./livechatDisplayUtils";
import { PASTEL } from "./livechatTheme";

// Riwayat order (FR-A-07/08) — reuse endpoint Lavender "profile-customer" yang sama dengan
// yang dipakai src/components/CustomerProfile/index.jsx#loadCustomerQna (data.data.lastOrder).
// Bukan endpoint POS terpisah — cuma order terakhir, bukan daftar riwayat lengkap.
//
// DEV-1198 — channel mitra-purchasing TIDAK pakai lookup ini (nomor HP mitra bukan nomor
// pelanggan di sistem order, hasilnya kalau ada cuma kebetulan cocok/salah data). Untuk channel
// itu, panel order dibaca dari `session.linkedOrderNumber` via endpoint order-info khusus
// (lihat OrderInfoSection di bawah).
export default function CustomerContextPanel({ session, onGenerateLink, onClose }) {
    const isMitraChannel = session?.brand === LIVECHAT_PARTNER_BRAND;
    // DEV-1227 (AC DEV-1222) — tanda & info customer tampil "dari layanan manapun SELAIN
    // Corporate"; mitra-purchasing juga bukan konteks customer Sales, jadi ikut dilewati.
    const isCorporateChannel = session?.brand === CORPORATE_BRAND;
    const [lastOrder, setLastOrder] = useState(null);
    const [isLoadingOrder, setIsLoadingOrder] = useState(false);

    useEffect(() => {
        if (isMitraChannel || !session?.customerPhone) {
            setLastOrder(null);
            return;
        }
        setIsLoadingOrder(true);
        setLastOrder(null);
        axios
            .get(Api.lavenderGetCustomerQna(session.customerPhone), Api.getRequestHeaderNoAuth())
            .then(({ data }) => setLastOrder(data?.data?.lastOrder || null))
            .catch(() => setLastOrder(null))
            .finally(() => setIsLoadingOrder(false));
    }, [isMitraChannel, session?.customerPhone]);

    if (!session) return null;

    return (
        <Box sx={{
            width: 280, flexShrink: 0, borderLeft: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
            display: "flex", flexDirection: "column",
        }}>
            <Box sx={{ p: 2.5, pt: 1.5, textAlign: "center", borderBottom: `1px solid ${PASTEL.border}`, position: "relative" }}>
                {onClose && (
                    <Tooltip title="Sembunyikan panel">
                        <IconButton
                            size="small" onClick={onClose}
                            sx={{ position: "absolute", top: 4, right: 4, color: "text.secondary" }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Avatar sx={{ bgcolor: getAvatarColor(session.customerName), width: 56, height: 56, fontSize: 20, mx: "auto", mb: 1 }}>
                    {getInitials(session.customerName)}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>{session.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">{session.customerPhone}</Typography>
            </Box>

            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Informasi Kontak
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Kanal" value={session.brand} />
                    {session.issueCategory && <Row label="Kategori Kendala" value={session.issueCategory} />}
                    <Row label="Chat pertama" value={Global.formatDateTimeToStrDateTime24(session.created_at)} />
                    {session.originUrl && <Row label="Halaman asal" value={session.originUrl} isLink />}
                    <Row
                        label="Status"
                        value={
                            <Chip
                                size="small"
                                label={session.status === "active" ? "Aktif" : "Selesai"}
                                sx={session.status === "active"
                                    ? { fontWeight: 600, mt: 0.25, backgroundColor: PASTEL.primary, color: "#fff" }
                                    : { fontWeight: 600, mt: 0.25 }}
                            />
                        }
                    />
                </Stack>

                {!isMitraChannel && !isCorporateChannel && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <CustomerFlagInsightSection session={session} />
                    </>
                )}

                <Divider sx={{ mb: 2 }} />

                {isMitraChannel ? (
                    <OrderInfoSection session={session} />
                ) : (
                    <>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Order Terakhir
                        </Typography>

                        {isLoadingOrder && (
                            <Box textAlign="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}

                        {!isLoadingOrder && !lastOrder && (
                            <Alert
                                severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                                sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                            >
                                Belum ada riwayat order untuk nomor ini.
                            </Alert>
                        )}

                        {!isLoadingOrder && lastOrder && (
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Box textAlign="center" sx={{ mb: 1.5 }}>
                                    <Avatar
                                        variant="rounded"
                                        src={`${Api.getLavenderUrl()}/${lastOrder?.cartLast?.image}?w=200&fit=crop&auto=format`}
                                        sx={{ width: 72, height: 72, mx: "auto", mb: 1 }}
                                    />
                                    {lastOrder?.cartLast?.product_code && (
                                        <Chip size="small" label={lastOrder.cartLast.product_code} variant="outlined" />
                                    )}
                                </Box>
                                <Stack spacing={1.25}>
                                    <Row
                                        label="Nomor CO"
                                        value={
                                            <Button
                                                size="small" variant="text" target="_blank" rel="noreferrer"
                                                href={`https://sakura.prestisa.id/orders/view/${lastOrder?.order_number}`}
                                                endIcon={<OpenInNewIcon sx={{ width: 12, height: 12 }} />}
                                                sx={{ fontSize: 12, textTransform: "none", p: 0, minWidth: 0, color: PASTEL.primary }}
                                            >
                                                {lastOrder?.order_number}
                                            </Button>
                                        }
                                    />
                                    <Row label="Nama Pengirim" value={lastOrder?.cartLast?.sender_name || "-"} />
                                    <Row label="Pembayaran" value={lastOrder?.payment_type || "-"} />
                                    <Row label="Total" value={`Rp ${new Intl.NumberFormat("id-ID").format(lastOrder?.total || 0)}`} />
                                    <Row label="Qty" value={lastOrder?.cartLast?.qty ?? "-"} />
                                </Stack>
                            </Box>
                        )}
                    </>
                )}

                <Divider sx={{ mb: 2 }} />

                <Button
                    size="small" variant="outlined" fullWidth onClick={onGenerateLink}
                    startIcon={<LinkIcon fontSize="small" />}
                    sx={{
                        textTransform: "none",
                        color: PASTEL.primary,
                        borderColor: PASTEL.primary,
                        "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                    }}
                >
                    Generate Link Lanjut Chat
                </Button>
            </Box>
        </Box>
    );
}

// DEV-1198 (AC-5/AC-5b) — panel order/status pembayaran/ringkasan revisi untuk channel
// mitra-purchasing, dibaca dari `session.linkedOrderNumber` (bukan customerPhone, lihat komentar
// di atas komponen utama). `revisionNote` bisa null walau statusnya "on revision" — banyak record
// lama tidak punya bentuk log yang cocok utk diekstrak (lihat livechat.order-info.service.ts).
function OrderInfoSection({ session }) {
    const [orderInfo, setOrderInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!session?._id) {
            setOrderInfo(null);
            return;
        }
        setIsLoading(true);
        setOrderInfo(null);
        livechatApi
            .getOrderInfo(session._id)
            .then(({ data }) => setOrderInfo(data?.data?.orderInfo || null))
            .catch(() => setOrderInfo(null))
            .finally(() => setIsLoading(false));
    }, [session?._id]);

    return (
        <>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Info Order Terkait
            </Typography>

            {isLoading && (
                <Box textAlign="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {!isLoading && !orderInfo && (
                <Alert
                    severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                >
                    {session.linkedOrderNumber
                        ? "Order yang dikaitkan tidak ditemukan."
                        : "Mitra tidak mengaitkan order ke percakapan ini."}
                </Alert>
            )}

            {!isLoading && orderInfo && (
                <Stack spacing={1.25} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Nomor PO" value={orderInfo.poId ? `PO-${orderInfo.poId}` : "-"} />
                    <Row label="Status PO" value={<Chip size="small" label={orderInfo.status} variant="outlined" />} />
                    <Row label="Pembayaran" value={orderInfo.paymentType || "-"} />
                    <Row label="Total" value={`Rp ${new Intl.NumberFormat("id-ID").format(orderInfo.total || 0)}`} />
                    <Row label="Jadwal Kirim" value={orderInfo.deliveryAt ? Global.formatDateTimeToStrDateTime24(orderInfo.deliveryAt) : "-"} />
                    {orderInfo.revisionNote && (
                        <Row label="Catatan Revisi" value={orderInfo.revisionNote} />
                    )}
                </Stack>
            )}
        </>
    );
}

// DEV-1227 (AC DEV-1222) — tanda customer (DEV-1224: cara bayar/kesediaan self-order) +
// info customer (DEV-1226: jenis customer/saldo poin EP/riwayat order), digabung 1 section
// supaya Sales lihat semuanya otomatis tanpa perlu tanya manual. Backend resolve customer_id
// dari `session.customerPhone` sendiri (lihat resolveCustomerIdFromBody, panel ini memang
// belum pernah tahu customer_id numerik - lihat catatan lastOrder di atas).
function CustomerFlagInsightSection({ session }) {
    const [flag, setFlag] = useState(null);
    const [insight, setInsight] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!session?.customerPhone) {
            setFlag(null);
            setInsight(null);
            return;
        }
        setIsLoading(true);
        Promise.all([
            livechatApi.getCustomerFlag(session.customerPhone).then(({ data }) => data?.data || null).catch(() => null),
            livechatApi.getCustomerInsight(session.customerPhone).then(({ data }) => data?.data || null).catch(() => null),
        ])
            .then(([flagData, insightData]) => {
                setFlag(flagData);
                setInsight(insightData);
            })
            .finally(() => setIsLoading(false));
    }, [session?.customerPhone]);

    const customerTypeLabel =
        insight?.customer_type === "company" ? "Corporate" : insight?.customer_type === "personal" ? "Personal" : "-";
    const paymentTypeLabel = flag?.payment_type === "cash" ? "Cash" : flag?.payment_type === "piutang" ? "Piutang" : "-";

    return (
        <>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Tanda Customer
            </Typography>

            {isLoading && (
                <Box textAlign="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {!isLoading && !flag && !insight && (
                <Alert
                    severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                >
                    Belum ada tanda untuk customer ini.
                </Alert>
            )}

            {!isLoading && (flag || insight) && (
                <Stack spacing={1.25} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Jenis Customer" value={customerTypeLabel} />
                    <Row label="Cara Bayar" value={paymentTypeLabel} />
                    <Row
                        label="Kesediaan Pesan Sendiri"
                        value={
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={
                                        flag?.self_order_status === "bersedia"
                                            ? "Bersedia"
                                            : flag?.self_order_status === "tidak_bersedia"
                                            ? "Tidak Bersedia"
                                            : "Belum Ditandai"
                                    }
                                    sx={{
                                        fontWeight: 600,
                                        mt: 0.25,
                                        ...(flag?.self_order_status === "bersedia"
                                            ? { backgroundColor: PASTEL.successSoft, color: PASTEL.success }
                                            : flag?.self_order_status === "tidak_bersedia"
                                            ? { backgroundColor: PASTEL.dangerSoft, color: PASTEL.danger }
                                            : {}),
                                    }}
                                />
                                {flag?.self_order_needs_review && (
                                    <Chip size="small" variant="outlined" label="Perlu ditinjau ulang" sx={{ mt: 0.25 }} />
                                )}
                            </Stack>
                        }
                    />
                    <Row
                        label="Saldo Poin EP"
                        value={`${new Intl.NumberFormat("id-ID").format(insight?.ep_point_balance ?? 0)} poin`}
                    />
                    {insight?.order_history && (
                        <Row label="Total Order" value={`${insight.order_history.total_orders ?? 0}x`} />
                    )}
                </Stack>
            )}
        </>
    );
}

function Row({ label, value, isLink }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            {isLink ? (
                <Typography
                    variant="body2" component="a" href={value} target="_blank" rel="noreferrer"
                    sx={{ display: "block", wordBreak: "break-all", color: PASTEL.primary }}
                >
                    {value}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight={500}>{value}</Typography>
            )}
        </Box>
    );
}
